export type PowerUnit = 'kW' | 'MW';
export type EnergyUnit = 'kWh' | 'MWh';

type NullableNumber = number | null | undefined;

const UNIT_CONVERSION_THRESHOLD = 1000;

const shouldUseLargeUnit = (value: number) => Math.abs(value) >= UNIT_CONVERSION_THRESHOLD;

export interface MeasurementDisplay<Unit extends string> {
  value: string;
  unit: Unit;
  text: string;
}

const formatMeasurement = <SmallUnit extends string, LargeUnit extends string>(
  value: NullableNumber,
  smallUnit: SmallUnit,
  largeUnit: LargeUnit
): MeasurementDisplay<SmallUnit | LargeUnit> => {
  if (typeof value !== 'number') {
    return { value: '--', unit: smallUnit, text: '--' };
  }

  const useLargeUnit = shouldUseLargeUnit(value);
  const unit = useLargeUnit ? largeUnit : smallUnit;
  const displayValue = (useLargeUnit ? value / UNIT_CONVERSION_THRESHOLD : value).toFixed(2);
  return { value: displayValue, unit, text: `${displayValue} ${unit}` };
};

export const formatPower = (value: NullableNumber) => formatMeasurement(value, 'kW', 'MW');

export const formatEnergy = (value: NullableNumber) => formatMeasurement(value, 'kWh', 'MWh');

export const getPowerUnit = (values: NullableNumber[]): PowerUnit =>
  values.some(value => typeof value === 'number' && shouldUseLargeUnit(value)) ? 'MW' : 'kW';

export const convertPower = (value: NullableNumber, unit: PowerUnit, precision = 2) =>
  typeof value === 'number'
    ? Number((unit === 'MW' ? value / UNIT_CONVERSION_THRESHOLD : value).toFixed(precision))
    : null;
